import { ROUTES } from 'app/utils/constants/paths';
import { getThumbnailUrl, getValidUrl, isVideolesson } from 'core';
import useAmplitude from 'core/hooks/useAmplitude';
import useDynamicPath from 'core/hooks/useDynamicPath';
import useEventsAnalytics from 'core/hooks/useEventsAnalytics';
import { useXapi } from 'core/hooks/useXapi';
import { useCourse } from 'core/lite/hooks/useCourse';
import { parseEducationYearName } from 'core/lite/parsers';
import CountBase from 'core/modules/atoms/Count';
import { getScrollBehavior } from 'core/utils/motion';
import Button from '../../../atoms/Button/Button';
import Hr from '../../../atoms/Hr/Hr';
import Loading from '../../../atoms/Loading/Loading';
import TextHtml from '../../../atoms/TextHtml/TextHtml';
import FeedBackUserBase from 'core/modules/components/general/FeedBackUser';
import HorizontalStudentsProgressBase from 'core/modules/components/general/HorizontalStudentsProgress';
import PreviewContentBase from 'core/modules/components/general/PreviewContent';
import SquareProgressBadgeBase from 'core/modules/components/general/SquareProgressBadge';
import VerticalStudentsResultsBase from 'core/modules/components/general/VerticalStudentsResults';
import AccordionList2Base from 'core/modules/components/lists/components/AccordionList2/AccordionList2';
import LessonElementItemBase from 'core/modules/components/lists/lessons/LessonElementItem/LessonElementItem';
import TwoPagePdfViewerBase from 'core/modules/components/pdfViewers/TwoPagePdfViewer/TwoPagePdfViewer';
import TeacherNotesViewBase from 'core/modules/compositions/TeacherNotesView';
import { getContentLang } from 'core/utils/contentLang';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { isTeacherItem, isTeacherOnlyItem } from 'core/utils/lesson';
import { isDisregardedProgram, isEditorial, isTeacher } from 'core/utils/user';
import { AMPLITUDE_EVENT, CONTENT_TYPES, getIdTypeContent } from 'core/variables/constant';
import { EVENTS_ANALYTICS } from 'core/variables/events-analytics';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

/**
 * LessonItemList - Lista de items (assignments/scorm/xapi/contenidos) de una lección,
 * renderizados como acordeones (`AccordionList2`, MUI con `aria-controls`/`id`). Reubicada
 * desde `core/modules/components/lists/lessons/LessonItemList` a `ui/shared/lists` (TAN-10165)
 * y tipada a TypeScript. Se preserva 1:1 el comportamiento y la a11y ya introducidos
 * (reduced-motion, idioma del visor, open_content). Consumida vía `import { LessonItemList } from 'ui/shared'`.
 */

// Componentes de dominio en JS (varios envueltos en `withTranslation`, que hace que TS infiera
// props restrictivas): se tipan como FCs permisivas para consumirlos por spread sin fricción de
// tipos, igual que el patrón de `ModulesList` en TAN-9854 (`as unknown as React.FC<...>`).
type DomainComponentProps = Record<string, unknown>;
const Count = CountBase as unknown as React.FC<DomainComponentProps>;
const FeedBackUser = FeedBackUserBase as unknown as React.FC<DomainComponentProps>;
const HorizontalStudentsProgress = HorizontalStudentsProgressBase as unknown as React.FC<DomainComponentProps>;
const PreviewContent = PreviewContentBase as unknown as React.FC<DomainComponentProps>;
const SquareProgressBadge = SquareProgressBadgeBase as unknown as React.FC<DomainComponentProps>;
const VerticalStudentsResults = VerticalStudentsResultsBase as unknown as React.FC<DomainComponentProps>;
const AccordionList2 = AccordionList2Base as unknown as React.FC<DomainComponentProps>;
const LessonElementItem = LessonElementItemBase as unknown as React.FC<DomainComponentProps>;
const TwoPagePdfViewer = TwoPagePdfViewerBase as unknown as React.FC<DomainComponentProps>;
const TeacherNotesView = TeacherNotesViewBase as unknown as React.FC<DomainComponentProps>;

type StudentScore = Readonly<{ score: number; turned?: boolean }> & Readonly<Record<string, unknown>>;

// Solo se leen/pasan estos campos aquí; el resto de props las consumen los items hijos
// (JS sin tipar) vía spread, cubiertas por el index signature `Record<string, unknown>`.
type LessonItem = Readonly<{
  guid: string;
  typeItem?: string;
  status?: string;
  content_guid?: string;
  content_type_guid?: string;
  content_name?: string;
  content_url?: string;
  content_thumbnail?: string;
  contentDescription?: string;
  comments?: unknown[];
  likes?: unknown;
  course_message_guid?: string;
  teacher_notes?: string;
  toStudents?: StudentScore[];
  students?: Array<Record<string, unknown>>;
  assigned?: number;
  turned?: number;
  graded?: number;
  averageTime?: number;
  item_for?: string;
  item_for_users?: unknown;
  item_jobs_total?: number;
  item_jobs_completed?: number;
  is_embed?: number;
  section_type_guid?: string;
  type?: string;
  author_name?: string;
  author_lastname?: string;
  author_lastName?: string;
  author_guid?: string;
  author_role_guid?: string;
  content_is_teacher_only?: number;
  didacticTypeName?: string;
  published_at?: string;
  deliver_at?: string;
  publish_score_at?: string;
  job_evaluated_by?: unknown;
}> &
  Readonly<Record<string, unknown>>;

type CurrentUser = Readonly<{ guid?: string; avatar?: string }> & Readonly<Record<string, unknown>>;
type Lesson = Readonly<{ image?: string }> & Readonly<Record<string, unknown>>;
type CurrentSession = Readonly<{ lesson_guid?: string }> & Readonly<Record<string, unknown>>;
type ScoreScale = Readonly<Record<string, unknown>>;

type LessonItemListProps = Readonly<{
  isLessonEditable?: boolean;
  lessonItems?: LessonItem[];
  onGetLessonItemMessages?: (assignment: LessonItem) => void | Promise<void>;
  currentUser?: CurrentUser;
  onPublishComment?: (...args: unknown[]) => void;
  onEditComment?: (...args: unknown[]) => void;
  onDeleteComment?: (...args: unknown[]) => void;
  // Sin default no-op a propósito: el original no los declaraba en `defaultProps`, así que
  // deben ser provistos por el consumidor. Marcados como requeridos para que, si faltan y se
  // invocan, lancen igual que en `main` (paridad) en vez de tragarse el click en silencio.
  onClickDeleteLessonItem: (item: LessonItem) => void;
  onClickEditLessonItem: (item: LessonItem) => void;
  onClickEditTest: (item: LessonItem, lessonGuid?: string, courseGuid?: string) => void;
  onClickSeeTestResult: (item: LessonItem, currentUser?: CurrentUser, courseGuid?: string) => void;
  onViewResultAssessment: (guid: string, lessonGuid?: string, courseGuid?: string, type?: string) => void;
  onViewResultScorm: (guid: string, lessonGuid?: string, courseGuid?: string) => void;
  onViewResultXapi: (guid: string, lessonGuid?: string, courseGuid?: string) => void;
  lesson?: Lesson;
  onClickViewContent?: (item: LessonItem) => void;
  loadingComment?: boolean;
  onShowVisor: (...args: unknown[]) => void;
  isStudent?: boolean;
  authToken?: string;
  updateJob?: (...args: unknown[]) => void;
  lessonItemGuidUrl?: string;
  isGuideline?: boolean;
  showBigContent?: boolean;
  currentSession?: CurrentSession | null;
  isUnoi?: boolean;
  scoreScale?: ScoreScale;
  isReferenced?: string;
}>;

// No cierra sobre estado/props del componente (solo su parámetro), así que vive en scope de
// módulo para no recrearse en cada render (SonarQube S7721).
function getAuthorName(item?: LessonItem): string | null {
  let authorName: string | null = null;
  if (item?.author_name) {
    authorName = item.author_name;
  }

  const lastName = item?.author_lastname ?? item?.author_lastName;
  if (lastName) {
    authorName = authorName ? `${authorName} ${lastName}` : lastName;
  }

  return authorName;
}

function LessonItemList({
  isLessonEditable,
  lessonItems = [],
  onGetLessonItemMessages = () => {},
  currentUser,
  onPublishComment = () => {},
  onEditComment = () => {},
  onDeleteComment = () => {},
  onClickDeleteLessonItem,
  onClickEditLessonItem,
  onClickEditTest,
  onClickSeeTestResult,
  onViewResultAssessment,
  onViewResultScorm,
  onViewResultXapi,
  lesson,
  onClickViewContent = () => {},
  loadingComment,
  onShowVisor,
  isStudent,
  authToken,
  updateJob = () => null,
  lessonItemGuidUrl,
  isGuideline,
  showBigContent,
  currentSession,
  isUnoi,
  scoreScale,
  isReferenced,
}: LessonItemListProps): JSX.Element {
  const { t } = useTranslation('lesson');
  const { courseGuid, lessonGuid } = useParams<{ courseGuid: string; lessonGuid: string }>();
  const [defaultExpandedLessonItem, setDefaultExpandedLessonItem] = useState<string | undefined>(lessonItemGuidUrl);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [nextPageNumber, setnextPageNumber] = React.useState(2);
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [selectedContent, setSelectedContent] = useState<string | null>(null);
  const [pauseContent, setPauseContent] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const { data: course } = useCourse(courseGuid) as { data?: Record<string, unknown> };
  const contentLang = getContentLang(course);
  useXapi();
  const { sendEventAnalytic } = useEventsAnalytics();
  const { getDynamicPath } = useDynamicPath();
  const { sendAmplitudeEvent } = useAmplitude();

  useEffect(() => {
    if (lessonItemGuidUrl) {
      setExpandedItems({ [lessonItemGuidUrl]: true });
      setDefaultExpandedLessonItem(lessonItemGuidUrl);
      const element = document.getElementById(lessonItemGuidUrl);
      setTimeout(() => {
        if (element) {
          element.scrollIntoView({ behavior: getScrollBehavior(), block: 'center' });
        }
      }, 1000);
    }
  }, [lessonItemGuidUrl]);

  function onChangeExpandedState(itemId: string, expanded: boolean) {
    setSelectedContent(expanded ? itemId : null);
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: expanded,
    }));

    setPauseContent(!expanded);
  }

  function renderChildrenAssessment(assessment: LessonItem, scoreScale?: ScoreScale) {
    let pending = 0;
    let students = assessment.toStudents;
    if (assessment?.toStudents) {
      students = students?.map((student) => {
        if (isVideolesson(assessment.content_type_guid)) {
          return {
            ...student,
            score: student.score * 10,
          };
        } else return student;
      });
      pending = assessment.toStudents.filter((item) => !item.turned).length;
    }
    return (
      <>
        {!isEditorial(currentUser) && !isTeacherOnlyItem(assessment) && (
          <>
            <Hr />
            <div className="lesson-item-list__children-counts">
              <Count number={assessment.assigned} text={t('lesson:Assigned')} />
              {isVideolesson(assessment.content_type_guid) ? (
                <Count number={pending || 0} text={t('lesson:Not turned it')} />
              ) : (
                <Count number={assessment.turned || 0} text={t('lesson:Turned it')} />
              )}
              <Count number={assessment.graded || 0} text={t('lesson:Graded')} />
            </div>
            <div className="lesson-item-list__children-container-graph">
              {assessment?.toStudents ? (
                <VerticalStudentsResults students={students} averageTime={assessment.averageTime} scoreScale={scoreScale} />
              ) : (
                <div className="lesson-item-list__children-loader">
                  <Loading hasBackdrop={false} isFullScreen={false} />
                </div>
              )}
            </div>
          </>
        )}
        {assessment.teacher_notes && !isStudent && (
          <div className="lesson-item-list__children__teacher-notes">
            <TeacherNotesView text={assessment.teacher_notes} />
          </div>
        )}
        {
          /*!isEditorial(currentUser) && isLessonEditable &&*/ <>
            <Hr />
            <div className="lesson-item-list__children-container-feedback">
              {canShowFeedback(assessment) ? (
                <FeedBackUser
                  isShowedComments={defaultExpandedLessonItem === assessment.guid}
                  loading={loadingComment}
                  currentUser={currentUser}
                  comments={assessment.comments}
                  commentsLength={assessment.comments?.length}
                  likes={assessment.likes}
                  right={
                    <button
                      type="button"
                      className="lesson-item-list__children-results-text"
                      onClick={() =>
                        onViewResultAssessment(assessment.guid, currentSession ? currentSession.lesson_guid : lessonGuid, courseGuid, assessment.type)
                      }
                    >
                      {t('View results')}
                    </button>
                  }
                  disabled={assessment.status === 'draft' || assessment.status === 'scheduled'}
                  onPublishComment={onPublishComment}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  guid={assessment.course_message_guid}
                />
              ) : (
                <div className="lesson-item-list__open-assessment">
                  <button
                    type="button"
                    className="lesson-item-list__children-results-text"
                    onClick={() =>
                      onViewResultAssessment(assessment.guid, currentSession ? currentSession.lesson_guid : lessonGuid, courseGuid, assessment.type)
                    }
                  >
                    {t('View Task')}
                  </button>
                </div>
              )}
            </div>
          </>
        }
      </>
    );
  }

  function renderChildrenScorm(item: LessonItem, scoreScale?: ScoreScale) {
    return (
      <div className="lesson-item-list__children-content-container">
        {!isEditorial(currentUser) && (
          <div className="lesson-item-list__children-content">
            {!isStudent && !isTeacherOnlyItem(item) && <Count number={t(`lesson:${item.item_for}`)} text={t('lesson:Assigned')} />}
          </div>
        )}
        <div className="scorm-results-overview__counters">
          <SquareProgressBadge progress={(item.item_jobs_total ?? 0) - (item.item_jobs_completed ?? 0)} total={item.item_jobs_total} text={t('scorm:Incomplete')} />
          <SquareProgressBadge progress={item.item_jobs_completed} total={item.item_jobs_total} text={t('scorm:Complete')} />
        </div>
        <div className="lesson-item-list__children-description">
          <span>{item.contentDescription}</span>
        </div>

        {item.teacher_notes && !isStudent && (
          <div className="lesson-item-list__children__teacher-notes">
            <TeacherNotesView text={item.teacher_notes} />
          </div>
        )}
        <div className="lesson-item-list__students-graph">
          <HorizontalStudentsProgress students={item.students || []} t={t} scoreScale={scoreScale} />
        </div>
        {
          <div className="lesson-item-list__children-container-feedback">
            {canShowFeedback(item) ? (
              <FeedBackUser
                isShowedComments={defaultExpandedLessonItem === item.guid}
                loading={loadingComment}
                currentUser={currentUser}
                comments={item.comments}
                commentsLength={item.comments?.length}
                likes={item.likes}
                right={
                  <div className="lesson-item-list__open-assessment">
                    <button
                      type="button"
                      className="lesson-item-list__children-results-text lesson-item-list__children-results-text-interactive"
                      onClick={() => onOpenScorm(item)}
                    >
                      {t('View Scorm')}
                    </button>
                    <button
                      type="button"
                      className="lesson-item-list__children-results-text"
                      onClick={() => onViewResultScorm(item.guid, currentSession ? currentSession.lesson_guid : lessonGuid, courseGuid)}
                    >
                      {t('View results')}
                    </button>
                  </div>
                }
                disabled={item.status === 'draft' || item.status === 'scheduled'}
                onPublishComment={onPublishComment}
                onEditComment={onEditComment}
                onDeleteComment={onDeleteComment}
                guid={item.course_message_guid}
              />
            ) : (
              <div className="lesson-item-list__open-assessment">
                <button
                  type="button"
                  className="lesson-item-list__children-results-text"
                  onClick={() => onViewResultScorm(item.guid, currentSession ? currentSession.lesson_guid : lessonGuid, courseGuid)}
                >
                  {t('View Scorm')}
                </button>
              </div>
            )}
          </div>
        }
      </div>
    );
  }

  function renderChildrenXapi(item: LessonItem, scoreScale?: ScoreScale) {
    return (
      <div className="lesson-item-list__children-content-container">
        {!isEditorial(currentUser) && (
          <div className="lesson-item-list__children-content">
            {!isStudent && !isTeacherOnlyItem(item) && <Count number={t(`lesson:${item.item_for}`)} text={t('lesson:Assigned')} />}
          </div>
        )}
        <div className="scorm-results-overview__counters">
          <SquareProgressBadge progress={(item.item_jobs_total ?? 0) - (item.item_jobs_completed ?? 0)} total={item.item_jobs_total} text={t('scorm:Incomplete')} />
          <SquareProgressBadge progress={item.item_jobs_completed} total={item.item_jobs_total} text={t('scorm:Complete')} />
        </div>
        <div className="lesson-item-list__children-description">
          <span>{item.contentDescription}</span>
        </div>

        {item.teacher_notes && !isStudent && (
          <div className="lesson-item-list__children__teacher-notes">
            <TeacherNotesView text={item.teacher_notes} />
          </div>
        )}
        <div className="lesson-item-list__students-graph">
          <HorizontalStudentsProgress students={item.students || []} t={t} scoreScale={scoreScale} />
        </div>
        {
          <div className="lesson-item-list__children-container-feedback">
            {canShowFeedback(item) ? (
              <>
                <div className="lesson-item-list__open-assessment">
                  <button
                    type="button"
                    className="lesson-item-list__children-results-text lesson-item-list__children-results-text-interactive"
                    onClick={() => onOpenXapi(item)}
                  >
                    {t('View interactive')}
                  </button>
                  <button
                    type="button"
                    className="lesson-item-list__children-results-text"
                    onClick={() => onViewResultXapi(item.guid, currentSession ? currentSession.lesson_guid : lessonGuid, courseGuid)}
                  >
                    {t('View results')}
                  </button>
                </div>
                <FeedBackUser
                  isShowedComments={defaultExpandedLessonItem === item.guid}
                  loading={loadingComment}
                  currentUser={currentUser}
                  comments={item.comments}
                  commentsLength={item.comments?.length}
                  likes={item.likes}
                  disabled={item.status === 'draft' || item.status === 'scheduled'}
                  onPublishComment={onPublishComment}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  guid={item.course_message_guid}
                />
              </>
            ) : (
              <div className="lesson-item-list__open-assessment">
                <button
                  type="button"
                  className="lesson-item-list__children-results-text lesson-item-list__children-results-text-interactive"
                  onClick={() => onOpenXapi(item)}
                >
                  {t('View interactive')}
                </button>
                <button
                  type="button"
                  className="lesson-item-list__children-results-text"
                  onClick={() => onViewResultXapi(item.guid, currentSession ? currentSession.lesson_guid : lessonGuid, courseGuid)}
                >
                  {t('View results')}
                </button>
              </div>
            )}
          </div>
        }
      </div>
    );
  }

  async function onOpenXapi(item: LessonItem) {
    sendAmplitudeEvent(
      AMPLITUDE_EVENT.OPEN_CONTENT,
      {
        content_type: 'XAPI',
        content_guid: item.content_guid,
        discipline: course?.discipline_name,
        content_stage: course?.education_level_name,
        content_grade: parseEducationYearName(course?.education_year_name),
      },
      course
    );

    sendEventAnalytic(
      EVENTS_ANALYTICS.OPEN_CONTENT,
      {
        content_type_guid: getIdTypeContent('xapi'),
        content_guid: item.content_guid,
        content_name: item.content_name,
        didactic_type: item.didacticTypeName,
        is_teacher_only: item.content_is_teacher_only === 0 ? false : true,
      },
      course
    );
    // const url = await onGetUrlXapi(item.content_guid);
    // window.open(url);
    const path = getDynamicPath(ROUTES.VIEWER_FULL_V1, { guid: item.content_guid }, { lessonItem: item.guid });
    window.open(path, '_blank');
  }

  async function onOpenScorm(item: LessonItem) {
    sendAmplitudeEvent(
      AMPLITUDE_EVENT.OPEN_CONTENT,
      {
        content_type: 'scorm',
        content_guid: item.content_guid,
        discipline: course?.discipline_name,
        content_stage: course?.education_level_name,
        content_grade: parseEducationYearName(course?.education_year_name),
      },
      course
    );

    sendEventAnalytic(
      EVENTS_ANALYTICS.OPEN_CONTENT,
      {
        content_type_guid: CONTENT_TYPES.SCORM,
        content_guid: item.content_guid,
        content_name: item.content_name,
        didactic_type: item.didacticTypeName,
        is_teacher_only: item.content_is_teacher_only === 0 ? false : true,
      },
      course
    );

    // const url = await onGetUrlXapi(item.content_guid);
    const path = getDynamicPath(ROUTES.VIEWER_FULL_V1, { guid: item.content_guid }, { lessonItem: item.guid });
    window.open(path, '_blank');
  }

  function getAuthorAvatar(userGuid?: string) {
    const users = (course?.users as Array<Record<string, unknown>> | undefined) || [];
    const user = users.find((user) => user.person_guid === userGuid);
    let avatar;
    if (!user && userGuid === currentUser?.guid) {
      avatar = currentUser?.avatar;
    } else {
      avatar = user?.avatar;
    }
    return avatar;
  }

  function canShowFeedback(item: LessonItem) {
    if ((isDisregardedProgram as (course?: unknown, user?: unknown) => boolean)(course, currentUser)) return false;
    return (!isEditorial(currentUser) && isLessonEditable) || item.section_type_guid === 'default';
  }

  function previewPdfContent(item: LessonItem) {
    const onClickDocument = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
      // setnextPageNumber(pageNumber < numPages + 1 ? pageNumber + 1 : null);
      setNumPages(numPages);
    };
    const onClickRight = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (nextPageNumber > (numPages ?? 0)) return;
      setPageNumber(pageNumber + 1);
      setnextPageNumber(nextPageNumber + 1);
    };
    const onClickLeft = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (pageNumber === 1) return;
      setPageNumber(pageNumber - 1);
      setnextPageNumber(nextPageNumber - 1);
    };

    return (
      <div className="lesson-element-item__big-preview-content" onClick={onClickDocument}>
        <Button
          className="lesson-element-item__navigation"
          variant="plain"
          isIconOnly
          leftIcon="chevron-left"
          ariaLabel={t('actions:Previous')}
          isDisabled={pageNumber === 1}
          onClick={onClickLeft}
        />
        <TwoPagePdfViewer url={item.content_url} page={pageNumber} onDocumentLoadSuccess={onDocumentLoadSuccess} numPages={numPages} />
        <Button
          className="lesson-element-item__navigation"
          variant="plain"
          isIconOnly
          leftIcon="chevron-right"
          ariaLabel={t('actions:Next')}
          isDisabled={nextPageNumber > (numPages ?? 0)}
          onClick={onClickRight}
        />
      </div>
    );
  }

  function renderChildrenContent(item: LessonItem) {
    return (
      <div className="lesson-item-list__children-content-container">
        {item.content_name?.startsWith('TEACHERGUIDE_') ? (
          previewPdfContent(item)
        ) : (
          <>
            {!isEditorial(currentUser) && isTeacherOnlyItem(item) && (
              <div className="lesson-item-list__children-content">
                {!isStudent && !isTeacherOnlyItem(item) && <Count number={t(`lesson:${item.item_for}`)} text={t('lesson:Assigned')} />}
              </div>
            )}
            {
              <div className="lesson-item-list__children-description">
                <TextHtml text={item.contentDescription} />
              </div>
            }
            <PreviewContent
              {...item}
              content={item}
              contentUrl={item && (getValidUrl as (url?: string, token?: string) => string)(item.content_url, authToken)}
              thumbnail={(getThumbnailUrl as (thumbnail?: string, token?: string) => string)(item?.content_thumbnail, authToken)}
              onShowVisor={onShowVisor}
              dialogImage={true}
              previewImage={true}
              updateJob={updateJob}
              courseGuid={courseGuid}
              pauseContent={item.guid === selectedContent ? pauseContent : false}
              showEmbed={item.is_embed === 1 ? true : false}
            />
            {item.teacher_notes && !isStudent && (
              <div className="lesson-item-list__children__teacher-notes">
                <TeacherNotesView text={item.teacher_notes} />
              </div>
            )}
            {canShowFeedback(item) && (
              <div className="lesson-item-list__children-container-feedback">
                <FeedBackUser
                  isShowedComments={defaultExpandedLessonItem === item.guid}
                  loading={loadingComment}
                  currentUser={currentUser}
                  comments={item.comments}
                  commentsLength={item.comments?.length}
                  likes={item.likes}
                  disabled={item.status === 'draft' || item.status === 'scheduled'}
                  onPublishComment={onPublishComment}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  guid={item.course_message_guid}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  function getParseAccordion() {
    return lessonItems.map((lessonItem) => {
      const authorName = getAuthorName(lessonItem);
      const authorAvatar = getAuthorAvatar(lessonItem?.author_guid);
      const contentCover = lessonItem?.content_thumbnail;

      return {
        assignment: lessonItem || null,
        defaultExpanded: defaultExpandedLessonItem === lessonItem.guid,
        hasInnerToggle: true,
        title: (
          <div className={`lesson-item-list__title-container`}>
            <LessonElementItem
              {...lessonItem}
              contentLang={contentLang}
              isLessonEditable={isLessonEditable}
              statusItem={lessonItem.status}
              contentGuid={lessonItem.content_guid}
              lessonGuid={currentSession ? currentSession.lesson_guid : lessonGuid}
              dateProgrammed={lessonItem.published_at}
              deliveredAt={lessonItem.deliver_at && lessonItem.deliver_at !== '0000-00-00 00:00:00' ? lessonItem.deliver_at : null}
              publishScoreAt={
                lessonItem.publish_score_at && lessonItem.publish_score_at !== '0000-00-00 00:00:00' ? lessonItem.publish_score_at : null
              }
              evaluatedBy={lessonItem.job_evaluated_by}
              typeComponent={lessonItem.typeItem}
              onClickEdit={() => {
                onClickEditTest(lessonItem, currentSession ? currentSession.lesson_guid : lessonGuid, courseGuid);
              }}
              onClickSeeTestResult={() => {
                onClickSeeTestResult(lessonItem, currentUser, courseGuid);
              }}
              onClickDeleteLessonItem={() => {
                onClickDeleteLessonItem(lessonItem);
              }}
              onClickEditLessonItem={() => {
                onClickEditLessonItem(lessonItem);
              }}
              onClickView={() => {
                onClickViewContent(lessonItem);
              }}
              isStudent={isStudent}
              isEditorial={isEditorial(currentUser)}
              lessonImage={isUnoi ? null : lesson?.image}
              isGuideline={isGuideline}
              showBigContent={showBigContent}
              pdfUrl={showBigContent ? lessonItem.content_url : null}
              scoreScale={scoreScale}
              courseGuid={courseGuid}
              isReferenced={isReferenced === COURSE_TYPES.REFERENCED}
              authorName={isTeacher(lessonItem?.author_role_guid) ? authorName : null}
              authorAvatar={authorAvatar}
              isItemEditable={isTeacherItem(lessonItem)}
              contentCover={contentCover}
              isTeacherOnly={isTeacherOnlyItem(lessonItem)}
              itemFor={lessonItem?.item_for}
              itemForUsers={lessonItem?.item_for_users}
            />
          </div>
        ),
        className: ` ${isStudent && lessonItem.typeItem === 'assigment' ? 'lesson-item-list--student-assignment' : ''} ${
          lessonItemGuidUrl === lessonItem.guid ? 'lesson-item-list--active-url' : ''
        }`,
        id: lessonItem.guid,
        children: expandedItems[lessonItem.guid] ? (
          <div className="lesson-item-list__children-container">
            {lessonItem.typeItem === 'assigment' && !isStudent && renderChildrenAssessment(lessonItem, scoreScale)}
            {lessonItem.typeItem !== 'assigment' && lessonItem.typeItem !== 'scorm' && lessonItem.typeItem !== 'xapi' && renderChildrenContent(lessonItem)}
            {lessonItem.typeItem === 'scorm' && !isStudent && renderChildrenScorm(lessonItem, scoreScale)}
            {lessonItem.typeItem === 'xapi' && !isStudent && renderChildrenXapi(lessonItem, scoreScale)}
            {(lessonItem.typeItem === 'assigment' || lessonItem.typeItem === 'scorm' || lessonItem.typeItem === 'xapi') &&
              isStudent &&
              renderChildrenContent(lessonItem)}
          </div>
        ) : (
          <div className="lesson-item-list__children-loader">
            <Loading hasBackdrop={false} isFullScreen={false} />
          </div>
        ),
      };
    });
  }

  return (
    <div className={`lesson-item-list ${isStudent ? 'lesson-item-list--student' : 'lesson-item-list--teacher'}`}>
      <AccordionList2 tabs={getParseAccordion()} onGetLessonItemMessages={onGetLessonItemMessages} onChangeOpenedTabs={onChangeExpandedState} />
    </div>
  );
}

export default LessonItemList;
